import { type HighlightResult } from '@algolia/client-search';
import clsx from 'clsx';
import parse, {
  domToReact,
  type DOMNode,
  type Element,
  type HTMLReactParserOptions,
} from 'html-react-parser';
import { ServingSizeIcon as RecipeOutsideServingSizeIcon } from '@hungryroot/components-pairing-card';
import { ThumbsUp, Coin } from '@hungryroot/icons';
import { Skeleton } from '@hungryroot/pho';
import { TransparentButton, Paragraph, Heading } from '@hungryroot/ramen';
import { InventoryStatus } from '@hungryroot/rest';
import { type PairingResult, type ProductResult } from '../../types';
import { SearchInventoryStatus } from '../SearchInventoryStatus/SearchInventoryStatus';
import styles from './SearchResult.module.css';

type PairingSearchResult = Pick<
  PairingResult,
  | 'index_dish_type'
  | 'featured_img_url'
  | '_highlightResult'
  | 'objectID'
  | 'name'
  | 'points'
  | 'index_ingredients'
  | 'num_ratings'
  | 'recipe_score'
  | 'servings'
>;
type ProductSearchResult = Pick<
  ProductResult,
  | 'brand_name'
  | 'card_image_url'
  | '_highlightResult'
  | 'objectID'
  | 'name'
  | 'points'
  | 'index_ingredients'
>;
export type SearchResult = PairingSearchResult | ProductSearchResult;

type Props =
  | {
      result: SearchResult;
      className?: string;
      active?: boolean;
      isLoading: false;
      status: InventoryStatus;
      dietaryRestrictions?: string[];
      customerPlanServingSize?: number;
    }
  | { isLoading: true };

export function SearchResult(props: Props) {
  const { isLoading } = props;

  if (isLoading) {
    return (
      <div
        aria-label="Loading"
        data-testid="search-result-loading"
        className={styles.container}
        key="loading"
      >
        <div className={styles.image}>
          <Skeleton className={styles['full-height']} />
        </div>
        <div className={styles.text}>
          <div className={styles.title}>
            <span>
              <Skeleton />
            </span>
          </div>
          <div>
            <Paragraph
              variant="caption"
              className={`${styles.caption} ${styles.inline}`}
            >
              <>
                <span>
                  <Skeleton />
                </span>
              </>
            </Paragraph>
          </div>
        </div>
      </div>
    );
  }

  const {
    result,
    className,
    active,
    status,
    dietaryRestrictions,
    customerPlanServingSize,
  } = props;

  const name = result._highlightResult?.name?.value || '';
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if ((domNode as Element)?.name === 'em') {
        return (
          <Heading variant="H6" className={styles.inline}>
            {domToReact((domNode as Element).children as DOMNode[], options)}
          </Heading>
        );
      }
    },
  };

  const cyPrefix = `search-result-${result.objectID}`;

  const backgroundImageUrl =
    (result as PairingSearchResult).featured_img_url ||
    (result as ProductSearchResult).card_image_url ||
    (result as PairingSearchResult).index_dish_type?.thumbnail_url;

  return (
    <TransparentButton className={styles['full-width']} ariaLabel={result.name}>
      <div
        data-testid={`search-result-${result.objectID}`}
        className={`${styles.container} ${
          active ? styles.active : ''
        } ${className}`}
        key={result.objectID}
      >
        <div className={styles.relative}>
          <div
            className={styles.image}
            style={{
              backgroundImage: backgroundImageUrl
                ? `url(${backgroundImageUrl})`
                : undefined,
            }}
          ></div>
          {status !== InventoryStatus.Ideal && (
            <div
              className={styles['image-overlay']}
              data-testid={`search-${result.objectID}-inventory-overlay`}
            ></div>
          )}
        </div>
        <div className={styles.text}>
          <div className={styles.title} data-testid={`${cyPrefix}-title`}>
            <span>{parse(name, options)}</span>
          </div>
          {((result as PairingSearchResult).num_ratings ?? 0) > 0 && (
            <div data-testid={`${cyPrefix}-ratings`} className={styles.flex}>
              <Paragraph variant="caption" className={styles.caption}>
                <>
                  <ThumbsUp />
                  <span>
                    {`${(
                      (result as PairingSearchResult).recipe_score as number
                    ).toFixed(0)}`}
                    %
                    {(result as PairingSearchResult).num_ratings &&
                      ` (${(result as PairingSearchResult).num_ratings})`}
                  </span>
                </>
              </Paragraph>
              {(result as PairingSearchResult).servings !== undefined && (
                <div data-testid={`${cyPrefix}-servings-outside-plan-icon`}>
                  <RecipeOutsideServingSizeIcon
                    pairingServingSize={
                      (result as PairingSearchResult).servings
                    }
                    customerPlanServingSize={customerPlanServingSize}
                    additionalText={`${
                      (result as PairingSearchResult).servings
                    } serv.`}
                  />
                </div>
              )}
            </div>
          )}
          <IngredientsHighlight result={result} />
          {(result as ProductSearchResult)?._highlightResult?.brand_name && (
            <div className={styles.caption} data-testid={`${cyPrefix}-brand`}>
              <span>
                {parse(
                  (
                    (result as ProductSearchResult)
                      ._highlightResult as HighlightResult<ProductSearchResult>
                  ).brand_name?.value as string,
                  options
                )}
              </span>
            </div>
          )}
          <SearchInventoryStatus
            testid={result.objectID}
            item={result}
            status={status}
            restrictions={dietaryRestrictions}
          />
        </div>
        {result.points && (
          <Heading variant="H6" className={styles.credits}>
            <Coin />
            <span
              className={styles['credits-space']}
              data-testid={`${cyPrefix}-points`}
            >
              {result.points}
            </span>
          </Heading>
        )}
      </div>
    </TransparentButton>
  );
}

interface IngredientsHighlightProps {
  result: SearchResult;
}

function IngredientsHighlight(props: IngredientsHighlightProps) {
  const { result } = props;

  const highlightText = getHighlightTextFromResult(result);

  if (!highlightText) {
    return null;
  }

  const parseOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if ((domNode as Element)?.name === 'em') {
        return (
          <span className={styles['inline-ingredient-highlight']}>
            {domToReact(
              (domNode as Element).children as DOMNode[],
              parseOptions
            )}
          </span>
        );
      }
    },
  };

  return (
    <Paragraph
      variant="caption"
      className={clsx(styles.caption, styles['ingredients-highlight'])}
    >
      Includes: <span>{parse(highlightText, parseOptions)}</span>
    </Paragraph>
  );
}

function getHighlightTextFromResult(result: SearchResult) {
  if (!result._highlightResult?.index_ingredients) {
    return '';
  }

  const listOfHighlights: string[] = [];

  for (const ingredientInfo of result._highlightResult.index_ingredients) {
    const ingredient = ingredientInfo?.[0];

    if (ingredient && ingredient.matchedWords?.length > 0 && ingredient.value) {
      listOfHighlights.push(ingredient.value);
    }
  }

  return listOfHighlights.join(', ');
}
